import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, Sparkles, ArrowRight, CheckCircle2, Zap, MessageSquare, ShoppingCart, Brain, Settings, Palette, Globe2, Shield, Send, User, Mail, Building, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const FORMSPREE_URL = 'https://formspree.io/f/xvzvpdwk';

// High quality images
const IMAGES = {
  hero: '/assets/build-agent-hero.jpg',
  fashion: '/assets/build-agent-fashion.jpg',
  electronics: '/assets/build-agent-electronics.jpg',
  beauty: '/assets/build-agent-beauty.jpg',
  home: '/assets/build-agent-home.jpg'
};

const BuildAgentPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiry_type: 'build_agent'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Formspree error');
      setIsSubmitted(true);
      toast.success('Thank you! Our team will contact you shortly.');
      setFormData({ name: '', email: '', company: '', message: '', inquiry_type: 'build_agent' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavClick = (href) => {
    navigate(href);
    window.scrollTo(0, 0);
  };

  const capabilities = [
    { icon: MessageSquare, title: 'Conversational AI', description: 'Natural language understanding for human-like conversations' },
    { icon: ShoppingCart, title: 'Product Discovery', description: 'Help customers find exactly what they need' },
    { icon: Brain, title: 'Smart Recommendations', description: 'AI-powered suggestions based on behavior' },
    { icon: Settings, title: 'Custom Workflows', description: 'Automate order tracking and support flows' },
    { icon: Palette, title: 'Brand Alignment', description: 'Match your brand voice and identity' },
    { icon: Globe2, title: 'Multi-language', description: 'Support customers in 50+ languages' }
  ];

  const industries = [
    { name: 'Fashion & Apparel', image: IMAGES.fashion },
    { name: 'Electronics', image: IMAGES.electronics },
    { name: 'Beauty & Skincare', image: IMAGES.beauty },
    { name: 'Home & Furniture', image: IMAGES.home }
  ];

  const steps = [
    { number: '01', title: 'Discovery Call', description: 'We learn about your business and goals' },
    { number: '02', title: 'Custom Design', description: 'We design your AI agent\'s personality' },
    { number: '03', title: 'Development', description: 'We build and train your custom AI' },
    { number: '04', title: 'Launch & Optimize', description: 'Go live with ongoing support' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="build-agent-page">
      <Navbar />
      
      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-lime-500 text-white rounded-full px-4 py-2 mb-6">
                <Bot className="w-4 h-4" />
                <span className="text-sm font-bold">Build Your E-commerce Agent</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
                Your Store Deserves a{' '}
                <span className="gradient-text">Custom AI Agent</span>
              </h1>
              <p className="text-xl text-slate-700 leading-relaxed mb-8">
                We build intelligent AI sales agents tailored specifically for your e-commerce business. From product recommendations to customer support, your AI agent handles it all.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#build-form">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white group w-full sm:w-auto font-bold text-base px-8 py-6">
                    Start Building
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto font-semibold border-2"
                  onClick={() => handleNavClick('/features')}
                >
                  See Capabilities
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="AI Assistant Technology"
                  className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-slate-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Zap className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-extrabold text-slate-900">24/7</p>
                      <p className="text-base text-slate-500 font-medium">Always Active</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-emerald-500 to-lime-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg">
                  AI Powered
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 rounded-full px-4 py-2 mb-6 shadow-sm">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-800">AI Capabilities</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-5">
                What Your Agent Can Do
              </h2>
              <p className="text-xl text-slate-700 max-w-2xl mx-auto leading-relaxed">
                Powerful AI capabilities designed specifically for e-commerce success
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-lg hover:shadow-xl hover:border-emerald-200 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-5">
                    <item.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-base leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-5">
                Built for Every Industry
              </h2>
              <p className="text-xl text-slate-700 max-w-2xl mx-auto leading-relaxed">
                Custom AI agents for fashion, electronics, beauty, home goods, and more
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.name}
                  className="group relative rounded-3xl overflow-hidden shadow-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-5">
                    <h3 className="text-white font-bold text-lg">{industry.name}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-24 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-5">
                How We Build Your Agent
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                A proven process to create the perfect AI agent for your store
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  className="bg-white rounded-3xl p-8 border-2 border-slate-100 relative hover:border-teal-200 hover:shadow-lg transition-all duration-300 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="text-6xl font-extrabold text-slate-100 absolute top-4 right-4">{step.number}</span>
                  <div className="relative pt-8">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 text-base">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Build Form */}
        <section id="build-form" className="py-24 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-5">
                Start Building Your Agent
              </h2>
              <p className="text-xl text-slate-700 leading-relaxed">
                Tell us about your business and we'll design the perfect AI agent for you
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="bg-emerald-50 rounded-3xl p-8 md:p-12 text-center border-2 border-emerald-200">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Request Received!</h3>
                  <p className="text-lg text-slate-600 mb-6">Our team will review your requirements and get back to you within 24 hours.</p>
                  <Button 
                    variant="outline" 
                    className="font-semibold"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-12 border-2 border-slate-200 shadow-xl" data-testid="build-agent-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-800 font-semibold mb-2 block text-base">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="pl-12 py-6 text-base bg-slate-50 border-slate-200"
                          data-testid="build-name-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-800 font-semibold mb-2 block text-base">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="pl-12 py-6 text-base bg-slate-50 border-slate-200"
                          data-testid="build-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="company" className="text-slate-800 font-semibold mb-2 block text-base">Company / Store Name</Label>
                    <div className="relative">
                      <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        placeholder="Your Store Name"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="pl-12 py-6 text-base bg-slate-50 border-slate-200"
                        data-testid="build-company-input"
                      />
                    </div>
                  </div>

                  <div className="mb-8">
                    <Label htmlFor="message" className="text-slate-800 font-semibold mb-2 block text-base">Tell Us About Your Needs</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Describe your store, products, target customers, and what you'd like your AI agent to do..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="resize-none text-base bg-slate-50 border-slate-200 p-4"
                      data-testid="build-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-bold"
                    disabled={isSubmitting}
                    data-testid="build-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Request Custom Agent Build
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BuildAgentPage;
